import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Form, Input, Select, DatePicker, Button, message, Spin, Card } from 'antd'
import { gmailService } from '../services/gmailService'
import apiClient from '../services/api'
import MaterialIcon from './MaterialIcon'
import dayjs from 'dayjs'

const { TextArea } = Input
const { Option } = Select

const FALLBACK_SERVICE_TYPES = [
  'Maintenance',
  'Repair',
  'Installation',
  'Inspection',
  'Emergency',
  'Preventive',
]

function formatServiceTypeLabel(value: string): string {
  const trimmed = (value || '').trim()
  if (!trimmed) return ''
  if (!trimmed.includes('_') && !trimmed.includes('-')) {
    if (trimmed === trimmed.toLowerCase()) {
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    }
    return trimmed
  }
  return trimmed
    .split(/[_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

function resolveServiceTypes(jobTypes: unknown): string[] {
  if (!Array.isArray(jobTypes)) return FALLBACK_SERVICE_TYPES
  const cleaned = jobTypes
    .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
    .filter(Boolean)
  return cleaned.length > 0 ? cleaned : FALLBACK_SERVICE_TYPES
}

interface CreateJobModalProps {
  visible: boolean
  onClose: () => void
  threadId: string
  customerId: string | null
  senderEmail?: string
}

const CreateJobModal: React.FC<CreateJobModalProps> = ({
  visible,
  onClose,
  threadId,
  customerId,
  senderEmail,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [extracting, setExtracting] = useState(false)
  const [serviceTypes, setServiceTypes] = useState<string[]>(FALLBACK_SERVICE_TYPES)

  const serviceTypeOptions = useMemo(
    () =>
      serviceTypes.map((type) => ({
        value: type,
        label: formatServiceTypeLabel(type),
      })),
    [serviceTypes]
  )

  // Load business service types + extract job data when modal opens
  useEffect(() => {
    if (!visible) return
    let cancelled = false
    ;(async () => {
      try {
        const response = await apiClient.get('/businesses/')
        const business = response.data
        if (!cancelled) {
          setServiceTypes(resolveServiceTypes(business?.jobTypes))
        }
      } catch {
        if (!cancelled) setServiceTypes(FALLBACK_SERVICE_TYPES)
      }
    })()
    if (threadId) {
      extractJobData()
    }
    return () => {
      cancelled = true
    }
  }, [visible, threadId])

  const extractJobData = async () => {
    try {
      setExtracting(true)
      console.log('🤖 Extracting job data...')
      
      const response = await gmailService.extractJob(threadId, senderEmail)
      const jobData = response.data

      console.log('✅ Extracted job data:', jobData)

      // Set form values
      form.setFieldsValue({
        serviceType: jobData.serviceType,
        title: jobData.title,
        description: jobData.description,
        urgency: jobData.urgency || 'normal',
        preferredDate: jobData.preferredDate ? dayjs(jobData.preferredDate) : null,
        serviceAddress: jobData.serviceAddress,
        specialInstructions: jobData.specialInstructions,
        estimatedDuration: jobData.estimatedDuration,
      })

      message.success('Job information extracted successfully!')
    } catch (error: any) {
      console.error('❌ Error extracting job data:', error)
      message.error(error.response?.data?.message || 'Failed to extract job information')
    } finally {
      setExtracting(false)
    }
  }

  const handleCreate = async () => {
    if (!customerId) {
      message.warning('Please create or link a customer first')
      return
    }

    try {
      setLoading(true)
      const values = await form.validateFields()

      const jobData = {
        ...values,
        preferredDate: values.preferredDate ? values.preferredDate.toISOString() : null,
      }

      console.log('📋 Creating job with data:', jobData)

      const response = await gmailService.createJob(threadId, customerId, jobData)

      message.success(response.message || 'Job created successfully!')
      form.resetFields()
      onClose()
      
      // Reload the page to show the updated job link
      setTimeout(() => {
        window.location.reload()
      }, 500)
    } catch (error: any) {
      console.error('❌ Error creating job:', error)
      message.error(error.response?.data?.message || 'Failed to create job')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={
        <span>
          <MaterialIcon icon="work" style={{ marginRight: '8px' }} />
          Create Service Call
        </span>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="extract"
          icon={<MaterialIcon icon="psychology" />}
          onClick={extractJobData}
          loading={extracting}
        >
          Re-Extract with AI
        </Button>,
        <Button
          key="create"
          type="primary"
          onClick={handleCreate}
          loading={loading}
          disabled={!customerId}
          icon={<MaterialIcon icon="add_task" />}
        >
          Create Job
        </Button>,
      ]}
      width={700}
    >
      {!customerId && (
        <Card
          style={{ marginBottom: '16px', background: '#fff7e6', borderColor: '#ffa940' }}
        >
          <MaterialIcon icon="warning" style={{ color: '#fa8c16', marginRight: '8px' }} />
          <strong>Customer Required:</strong> Please create or link a customer before creating a job.
        </Card>
      )}

      <Spin spinning={extracting} tip="Extracting job information with AI...">
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            urgency: 'normal',
          }}
        >
          <Form.Item
            label="Job Title"
            name="title"
            rules={[{ required: true, message: 'Please enter a job title' }]}
          >
            <Input
              placeholder="e.g., Garage Door Repair, HVAC Installation"
              prefix={<MaterialIcon icon="title" />}
            />
          </Form.Item>

          <Form.Item
            label="Service Type"
            name="serviceType"
          >
            <Select
              placeholder="Select service type"
              suffixIcon={<MaterialIcon icon="category" />}
              options={serviceTypeOptions}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <TextArea
              rows={4}
              placeholder="Detailed description of the service request..."
            />
          </Form.Item>

          <Form.Item
            label="Priority/Urgency"
            name="urgency"
            rules={[{ required: true, message: 'Please select urgency' }]}
          >
            <Select suffixIcon={<MaterialIcon icon="priority_high" />}>
              <Option value="normal">
                <MaterialIcon icon="radio_button_unchecked" style={{ marginRight: '8px', color: '#52c41a' }} />
                Normal
              </Option>
              <Option value="urgent">
                <MaterialIcon icon="error_outline" style={{ marginRight: '8px', color: '#faad14' }} />
                Urgent
              </Option>
              <Option value="emergency">
                <MaterialIcon icon="report_problem" style={{ marginRight: '8px', color: '#ff4d4f' }} />
                Emergency
              </Option>
            </Select>
          </Form.Item>

          <Form.Item
            label="Preferred Date/Time"
            name="preferredDate"
          >
            <DatePicker
              showTime
              style={{ width: '100%' }}
              format="YYYY-MM-DD HH:mm"
              placeholder="Select preferred date and time"
              suffixIcon={<MaterialIcon icon="event" />}
              disabledDate={(current) => {
                // Disable dates before today (compare via Date to avoid Dayjs type mismatch with antd)
                const todayStart = dayjs().startOf('day').toDate()
                return current ? current.toDate() < todayStart : false
              }}
              disabledTime={(current) => {
                const isToday = current && dayjs(current.toDate()).isSame(new Date(), 'day')
                
                // Only disable past times if today is selected
                if (isToday) {
                  const now = dayjs()
                  return {
                    disabledHours: () => {
                      const hours = []
                      for (let i = 0; i < now.hour(); i++) {
                        hours.push(i)
                      }
                      return hours
                    },
                    disabledMinutes: (selectedHour: number) => {
                      if (selectedHour === now.hour()) {
                        const minutes = []
                        for (let i = 0; i < now.minute(); i++) {
                          minutes.push(i)
                        }
                        return minutes
                      }
                      return []
                    }
                  }
                }
                return {}
              }}
            />
          </Form.Item>

          <Form.Item
            label="Service Address"
            name="serviceAddress"
            extra="Leave blank to use customer's address"
          >
            <Input
              placeholder="e.g., 123 Main St, City, State ZIP"
              prefix={<MaterialIcon icon="location_on" />}
            />
          </Form.Item>

          <Form.Item
            label="Estimated Duration (hours)"
            name="estimatedDuration"
          >
            <Input
              type="number"
              min={0.5}
              step={0.5}
              placeholder="e.g., 2"
              prefix={<MaterialIcon icon="schedule" />}
            />
          </Form.Item>

          <Form.Item
            label="Special Instructions"
            name="specialInstructions"
          >
            <TextArea
              rows={3}
              placeholder="Any special requirements, access instructions, or notes..."
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  )
}

export default CreateJobModal

